import React, { useRef } from 'react';

import { bemCssModules, getTestId } from '@utils';
import { useModal } from '@utils/hooks';

import { EUserStatuses } from '@modules/transactions/types';
import { TTransaction } from '@modules/transactions/types2';

import Details from './Details';
import OverflowCell from './OverflowCell';
import Time from './Time';
import useRowData from './useRowData';

import style from './Row.scss';

const cn = bemCssModules(style, 'Row');
const ti = getTestId('Row');

export type TRow = {
  transaction: TTransaction;
};

const Row: CustomFC<TRow> = ({ className, transaction }) => {
  const { created_at_ts, type } = transaction;

  const data = useRowData(transaction);

  const buttonRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const [showDetails, setShowDetails, modalRef] = useModal([buttonRef, detailsRef]);

  const onClose = () => setShowDetails(!showDetails);

  const isFailed = data.status === EUserStatuses.failed || data.status === EUserStatuses.cancelled;

  return (
    <>
      <div
        className={cn(null, [className])}
        ref={buttonRef}
        data-testid={ti()}
        onClick={onClose}
        role="button"
        tabIndex={0}
        onKeyDown={onClose}
      >
        <div className={cn('group')}>
          <Time timestamp={created_at_ts} type={type} isFailed={isFailed} status={data.status} />
          <OverflowCell column={2} error={isFailed}>
            {data.info}
          </OverflowCell>
        </div>
        <div className={cn('group')}>
          <OverflowCell column={3} error={isFailed}>
            <span data-testid={ti('fiat-sum')}>{data.fiat}</span>
          </OverflowCell>
          <OverflowCell column={4} error={isFailed}>
            <span data-testid={ti('crypto-sum')}>{data.crypto}</span>
          </OverflowCell>
        </div>
      </div>
      <Details
        className={cn('details')}
        ref={detailsRef}
        isOpen={showDetails}
        onClose={onClose}
        data={data}
        modalRef={modalRef}
        isFailed={isFailed}
      />
      <div className={cn('divider')} />
    </>
  );
};

export default Row;
